import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { stopLenis, startLenis } from "@/lib/lenis";

const API = process.env.REACT_APP_BACKEND_URL;
const MODELS = [
  { id: "chatgpt", label: "ChatGPT" },
  { id: "claude", label: "Claude" },
  { id: "gemini", label: "Gemini" },
];

const getSessionId = () => {
  let sid = localStorage.getItem("vp_concierge_sid");
  if (!sid) {
    sid = `guest-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("vp_concierge_sid", sid);
  }
  return sid;
};

const renderMd = (text) =>
  text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
    return p;
  });

export default function ConciergeChat() {
  const { t, lang } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState("chatgpt");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open || messages.length) return;
    fetch(`${API}/api/concierge/history/${sessionId.current}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((msgs) => setMessages(msgs))
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    if (!open) return;
    // On phones the panel is near-fullscreen — lock the page behind it so touch scroll targets the chat
    const mobile = window.matchMedia("(max-width: 639px)").matches;
    if (mobile) {
      stopLenis();
    }
    return () => {
      if (mobile) {
        startLenis();
      }
    };
  }, [open]);

  const send = async () => {    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/api/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, model, lang }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.t };
                return copy;
              });
            } else if (evt.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: evt.error };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("concierge.error") };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <>
      <button
        data-testid="concierge-open-btn"
        onClick={() => setOpen(!open)}
        aria-label={t("concierge.title")}
        className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-40 w-14 h-14 rounded-full bg-terracotta text-sand shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed left-3 right-3 top-16 bottom-40 sm:left-auto sm:top-auto sm:right-6 sm:bottom-24 sm:w-full sm:max-w-sm sm:h-[min(560px,calc(100dvh-8rem))] bg-white rounded-3xl border border-terracotta/15 shadow-2xl overflow-hidden flex flex-col z-40"
            data-testid="concierge-panel"
          >
            <div className="bg-night text-sand px-4 sm:px-5 py-3.5 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center shrink-0">
                <Sparkles size={16} />
              </span>
              <div className="min-w-0 shrink">
                <p className="font-serif text-base sm:text-lg leading-tight truncate">{t("concierge.title")}</p>
                <p className="text-[11px] text-sand/60 truncate hidden min-[420px]:block">{t("concierge.subtitle")}</p>
              </div>
              <div className="ml-auto flex rounded-full border border-sand/20 overflow-hidden shrink-0" data-testid="concierge-model-picker">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`concierge-model-${m.id}`}
                    onClick={() => setModel(m.id)}
                    disabled={busy}
                    className={`px-2.5 py-1 text-[10px] font-semibold transition-colors disabled:opacity-50 ${model === m.id ? "bg-gold text-night" : "text-sand/60 hover:text-sand"}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div ref={scrollRef} className="grow overflow-y-auto px-4 py-4 space-y-3" data-testid="concierge-messages" data-lenis-prevent style={{ WebkitOverflowScrolling: "touch" }}>
              {messages.length === 0 && (
                <div className="bg-sand rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-ink/75 leading-relaxed max-w-[85%]">
                  {t("concierge.greeting")}
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    m.role === "user"
                      ? "ml-auto bg-terracotta text-sand rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%]"
                      : "bg-sand rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-ink/80 leading-relaxed max-w-[85%] whitespace-pre-wrap"
                  }
                  data-testid={`concierge-msg-${i}`}
                >
                  {m.content ? (m.role === "assistant" ? renderMd(m.content) : m.content) : busy && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1 items-center" data-testid="concierge-typing">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  ) : ""}
                </div>
              ))}
            </div>

            <div className="border-t border-ink/10 p-3 flex gap-2">
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t("concierge.placeholder")}
                className="grow rounded-full bg-sand border border-ink/10 px-4 py-2.5 text-sm outline-none focus:border-terracotta"
              />
              <button
                data-testid="concierge-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                aria-label={t("concierge.send")}
                className="w-10 h-10 rounded-full bg-terracotta text-sand flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform shrink-0"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
